import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Me, UpdateUser } from "../api/users";

function errorText(payload, fallback) {
  if (!payload) return fallback;
  if (typeof payload === "string") return payload;
  if (typeof payload.detail === "string") return payload.detail;
  if (Array.isArray(payload.detail) && payload.detail[0]?.msg) {
    return payload.detail[0].msg;
  }
  return fallback;
}

function passwordStrength(value) {
  if (!value) return { level: 0, label: "Введите пароль" };
  const hasLetter = /[A-Za-zА-Яа-яЁё]/.test(value);
  const hasDigit = /\d/.test(value);
  if (value.length < 8) return { level: 1, label: "Слишком короткий" };
  if (value.length >= 12 && hasLetter && hasDigit) {
    return { level: 3, label: "Надёжный" };
  }
  if (hasLetter && hasDigit) return { level: 2, label: "Средний" };
  return { level: 2, label: "Можно сильнее" };
}

function LockMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M7 11V8a5 5 0 0 1 10 0v3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
      />
      <rect
        x="5"
        y="11"
        width="14"
        height="10"
        rx="2.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
      />
      <circle cx="12" cy="16" r="1.35" fill="currentColor" />
    </svg>
  );
}

function EyeIcon({ open }) {
  return open ? (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M3 12s3.6-7 9-7 9 7 9 7-3.6 7-9 7-9-7-9-7z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <circle
        cx="12"
        cy="12"
        r="3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M3 12s3.6-7 9-7c2 0 3.7.6 5.1 1.5M21 12s-3.6 7-9 7c-2 0-3.7-.6-5.1-1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M9.5 9.6A3 3 0 0 1 14.4 14.4M4 4l16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

const ChangeYourPassword = ({ token }) => {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [user, setUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const me = await Me(token);
        if (!cancelled) setUser(me);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setMessage({ ok: false, text: "Не удалось загрузить профиль" });
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const checks = useMemo(() => {
    const hasValue = password.length > 0 || confirm.length > 0;
    return {
      length: password.length >= 8,
      match: password.length > 0 && password === confirm,
      started: hasValue,
    };
  }, [password, confirm]);

  const strength = useMemo(() => passwordStrength(password), [password]);
  const isValid = checks.length && checks.match && user?.id;

  async function editPassword(event) {
    event.preventDefault();
    if (!isValid || isSubmitting) return;

    setIsSubmitting(true);
    setMessage(null);
    try {
      const data = await UpdateUser(token, {
        id: user.id,
        hashed_password: password,
      });
      if (data?.detail && !Number.isFinite(data?.id)) {
        setMessage({
          ok: false,
          text: errorText(data, "Не удалось сменить пароль"),
        });
        return;
      }
      setPassword("");
      setConfirm("");
      setShowPassword(false);
      setMessage({ ok: true, text: "Пароль изменён. Используйте его при следующем входе." });
    } catch (err) {
      console.error(err);
      setMessage({ ok: false, text: "Не удалось сменить пароль" });
    } finally {
      setIsSubmitting(false);
    }
  }

  const userLabel = user
    ? `${user.surname ?? ""} ${user.name ?? ""}`.trim() || user.username
    : "";
  const initials = user
    ? `${(user.surname?.[0] || "").toUpperCase()}${(user.name?.[0] || "").toUpperCase()}`
    : "";

  return (
    <div className="login-page password-page">
      <div className="login-card password-card">
        <div className="login-brand">
          <span className="login-brand-mark password-brand-mark">
            <LockMark />
          </span>
          <div>
            <p className="login-brand-name">Schedule</p>
            <p className="login-brand-sub">Безопасность аккаунта</p>
          </div>
        </div>

        <h1 className="login-title">Смена пароля</h1>
        <p className="login-hint">
          Придумайте новый пароль для входа. Старый больше не сработает.
        </p>

        <div className={`password-user${user ? "" : " is-loading"}`}>
          <span className="password-user-avatar">{initials || "…"}</span>
          <div>
            <p className="password-user-name">
              {userLabel || "Загрузка профиля…"}
            </p>
            <p className="password-user-meta">
              {user?.username ? `@${user.username}` : "Проверяем данные"}
            </p>
          </div>
        </div>

        {message ? (
          <div className={message.ok ? "login-success" : "login-error"}>
            {message.text}
          </div>
        ) : null}

        <form className="login-form" onSubmit={editPassword}>
          <div className="login-field">
            <label htmlFor="new-password">Новый пароль</label>
            <div className="password-input">
              <input
                id="new-password"
                type={showPassword ? "text" : "password"}
                className="form-control"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                placeholder="Не меньше 8 символов"
              />
              <button
                type="button"
                className="password-eye"
                onClick={() => setShowPassword((open) => !open)}
                aria-label={showPassword ? "Скрыть пароль" : "Показать пароль"}
                aria-pressed={showPassword}
              >
                <EyeIcon open={showPassword} />
              </button>
            </div>
            <div
              className={`password-strength is-level-${strength.level}`}
              aria-live="polite"
            >
              <div className="password-strength-bars" aria-hidden="true">
                <span />
                <span />
                <span />
              </div>
              <p className="password-strength-label">{strength.label}</p>
            </div>
          </div>

          <div className="login-field">
            <label htmlFor="confirm-password">Подтверждение</label>
            <div className="password-input">
              <input
                id="confirm-password"
                type={showPassword ? "text" : "password"}
                className="form-control"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                autoComplete="new-password"
                placeholder="Повторите пароль"
              />
            </div>
          </div>

          <ul className="password-checks">
            <li
              className={
                checks.length ? "is-ok" : checks.started ? "is-bad" : ""
              }
            >
              Не меньше 8 символов
            </li>
            <li
              className={
                checks.match ? "is-ok" : checks.started ? "is-bad" : ""
              }
            >
              Пароли совпадают
            </li>
          </ul>

          <button
            type="submit"
            className="btn login-submit"
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting ? "Сохранение…" : "Сохранить пароль"}
          </button>
        </form>

        <Link to="/profile" className="password-back">
          ← К профилю
        </Link>
      </div>
    </div>
  );
};

export default ChangeYourPassword;
